/**
 * Insurance catalog hook
 *
 * Loads and normalizes all visible rows from the Firestore `insurances` collection
 * for the insurer selection screen.
 *
 * References:
 * - `insurance/normalize.ts`
 */

import type { Insurance } from '@physiofit-digital/physiofit_types';
import { collection, getDocs } from 'firebase/firestore';
import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import { useCallback, useEffect, useState } from 'react';
import { db } from '../../../firebaseConfig';
import { normalizeInsuranceFromFirestore } from '../insurance/normalize';

function parseInsuranceDoc(doc: QueryDocumentSnapshot<DocumentData>): Insurance | null {
  return normalizeInsuranceFromFirestore(doc.id, doc.data() as Record<string, unknown>);
}

async function readInsurancesFromFirestore(): Promise<Insurance[]> {
  const snapshot = await getDocs(collection(db, 'insurances'));
  const next: Insurance[] = [];
  snapshot.forEach((d) => {
    const row = parseInsuranceDoc(d);
    if (row) {
      next.push(row);
    }
  });
  return next;
}

/**
 * Provides the insurer list with loading, error, and manual reload.
 *
 * @return {Object} `insurances`, `loading`, `error`, and `reload` callback.
 */
export function useInsuranceCatalog() {
  const [insurances, setInsurances] = useState<Insurance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const next = await readInsurancesFromFirestore();
        if (!cancelled) {
          setInsurances(next);
        }
      } catch {
        if (!cancelled) {
          setError('Die Krankenkassen konnten nicht geladen werden.');
          setInsurances([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setInsurances(await readInsurancesFromFirestore());
    } catch {
      setError('Die Krankenkassen konnten nicht geladen werden.');
      setInsurances([]);
    } finally {
      setLoading(false);
    }
  }, []);

  return { insurances, loading, error, reload };
}
